import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, SlidersHorizontal, Grid3X3, List, Check, X } from 'lucide-react';
import { getMachines, getAccessories } from '../../services/productApi';

/**
 * PWA All Products - Zeigt ALLE Produkte aus allen Kategorien
 * Mit Filter nach Kategorie und Sortierung
 */
const PWAAllProducts = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const [activeFilter, setActiveFilter] = useState('alle');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const navigate = useNavigate();

  // Kategorien für Filter
  const categories = [
    { id: 'alle', label: 'Alle Produkte' },
    { id: 'bagger', label: 'Bagger', dataCategory: ['Mini-Bagger'] },
    // { id: 'radlader', label: 'Radlader', dataCategory: ['Radlader'] }, // Ausgeblendet
    { id: 'gabelstapler', label: 'Gabelstapler', dataCategory: ['Gabelstapler'] },
    { id: 'ruettelplatten', label: 'Rüttelplatten', dataCategory: ['Rüttelplatte', 'Rüttelplatten'] },
    { id: 'stampfer', label: 'Stampfer', dataCategory: ['Stampfer'] },
    { id: 'zubehoer', label: 'Zubehör', dataCategory: ['Zubehör'], isAccessory: true },
  ];

  useEffect(() => {
    const loadAllProducts = async () => {
      setLoading(true);
      try {
        const [machineData, accessoryData] = await Promise.all([
          getMachines(),
          getAccessories()
        ]);

        const machines = machineData || [];
        const accessories = (accessoryData || []).map(a => ({ ...a, isAccessory: true, category: 'Zubehör' }));

        const combined = [...machines, ...accessories];
        setAllProducts(combined);
        setFilteredProducts(combined);
      } catch (error) {
        console.error('Error loading products:', error);
        setAllProducts([]);
        setFilteredProducts([]);
      } finally {
        setLoading(false);
      }
    };
    loadAllProducts();
  }, []);

  // Filter products when activeFilter changes
  useEffect(() => {
    if (activeFilter === 'alle') {
      setFilteredProducts(allProducts);
    } else {
      const category = categories.find(c => c.id === activeFilter);
      if (category?.dataCategory) {
        const filtered = allProducts.filter(p => 
          category.dataCategory.includes(p.category)
        );
        setFilteredProducts(filtered);
      }
    }
  }, [activeFilter, allProducts]);

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const getPriceValue = (item) => {
      if (item.isAccessory || item.price) {
        return item.price || 0;
      }
      return parseInt(item.pricing?.buy?.replace(/\D/g, '') || '0');
    };
    
    if (sortBy === 'price-asc') {
      return getPriceValue(a) - getPriceValue(b);
    }
    if (sortBy === 'price-desc') {
      return getPriceValue(b) - getPriceValue(a);
    }
    return 0;
  });

  // Get price display
  const getPrice = (item) => {
    if (item.isAccessory || item.price) {
      return item.price ? `${item.price.toLocaleString('de-DE')} €` : 'Auf Anfrage';
    }
    return item.pricing?.buy || 'Auf Anfrage';
  };

  // Get product link based on category
  const getProductLink = (item) => {
    if (item.isAccessory) {
      return `/zubehoer/${item.slug}`;
    }
    
    // Map category to URL path
    const categoryPathMap = {
      'Mini-Bagger': '/bagger',
      'Radlader': '/radlader',
      'Gabelstapler': '/gabelstapler',
      'Rüttelplatte': '/ruettelplatten',
      'Rüttelplatten': '/ruettelplatten',
      'Stampfer': '/stampfer',
    };
    
    const basePath = categoryPathMap[item.category] || '/bagger';
    return `${basePath}/${item.slug}`;
  };

  // Get category label for display
  const getCategoryLabel = (item) => {
    if (item.isAccessory) return 'Zubehör';
    const categoryLabelMap = {
      'Mini-Bagger': 'Bagger',
      'Radlader': 'Radlader',
      'Gabelstapler': 'Gabelstapler',
      'Rüttelplatte': 'Rüttelplatte',
      'Rüttelplatten': 'Rüttelplatte',
      'Stampfer': 'Stampfer',
    };
    return categoryLabelMap[item.category] || item.category;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="flex items-center h-14 px-4">
          <button 
            onClick={() => navigate('/')}
            className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full active:bg-gray-100"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="flex-1 text-center font-bold text-lg text-gray-900">Alle Produkte</h1>
          <div className="w-10" />
        </div>
        
        {/* Category Filter Pills */}
        <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveFilter(cat.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeFilter === cat.id
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 active:bg-gray-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Sort & View Bar */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-50">
          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm text-gray-700 font-medium px-3 py-1.5 rounded-lg bg-gray-100 border-0 focus:ring-0"
          >
            <option value="default">Sortierung</option>
            <option value="price-asc">Preis aufsteigend</option>
            <option value="price-desc">Preis absteigend</option>
          </select>
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            >
              <Grid3X3 size={18} className={viewMode === 'grid' ? 'text-gray-900' : 'text-gray-400'} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              <List size={18} className={viewMode === 'list' ? 'text-gray-900' : 'text-gray-400'} />
            </button>
          </div>
        </div>
      </header>

      {/* Results Count */}
      <div className="px-4 py-3 border-b border-gray-100">
        <p className="text-sm text-gray-500">
          {sortedProducts.length} Produkte
          {activeFilter !== 'alle' && ` in "${categories.find(c => c.id === activeFilter)?.label}"`}
        </p>
      </div>

      {/* Product Grid */}
      {viewMode === 'grid' ? (
        <div className="p-4 grid grid-cols-2 gap-3">
          {sortedProducts.map((product, index) => (
            <Link
              key={product.id || product.slug || index}
              to={getProductLink(product)}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden active:scale-[0.98] transition-transform shadow-sm"
            >
              {/* Category & Availability Badge */}
              <div className="px-3 pt-3 flex items-center justify-between">
                <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide">
                  {getCategoryLabel(product)}
                </span>
                <span className="inline-flex items-center text-[9px] font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                  <Check size={8} className="mr-0.5" />
                  Verfügbar
                </span>
              </div>
              
              {/* Image */}
              <div className="h-24 flex items-center justify-center p-2">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              
              {/* Info */}
              <div className="p-3 pt-0 border-t border-gray-50">
                <h3 className="font-bold text-gray-900 text-sm line-clamp-2">{product.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-base font-bold text-red-600">{getPrice(product)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {sortedProducts.map((product, index) => (
            <Link
              key={product.id || product.slug || index}
              to={getProductLink(product)}
              className="flex p-4 active:bg-gray-50 transition-colors"
            >
              {/* Image */}
              <div className="w-20 h-20 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="max-h-full max-w-full object-contain p-1"
                />
              </div>
              
              {/* Info */}
              <div className="flex-1 ml-3 flex flex-col justify-between py-0.5">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                        {getCategoryLabel(product)}
                      </span>
                      <h3 className="font-bold text-gray-900 line-clamp-1">{product.name}</h3>
                    </div>
                    <span className="text-[9px] font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded flex-shrink-0">
                      Verfügbar
                    </span>
                  </div>
                </div>
                <p className="text-lg font-bold text-red-600">{getPrice(product)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {sortedProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <p className="text-gray-500 text-center">Keine Produkte gefunden</p>
          <button 
            onClick={() => setActiveFilter('alle')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg font-medium"
          >
            Alle Produkte anzeigen
          </button>
        </div>
      )}
    </div>
  );
};

export default PWAAllProducts;
